import { useState, useEffect, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// Matches the state key set by ProtectedRoute when it bounces an unauth'd
// user to /login — we send them back to where they tried to go.
interface LocationState {
  from?: { pathname: string };
}

export default function Login() {
  useEffect(() => { document.title = "Sign In — PREP POS"; }, []);
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Open-redirect-safe `?next=` precedence over location.state.from.
  // The "Sign up to continue" handoff from StepPOS.tsx + the marketing-
  // portal TrialConversion CTAs both land here with ?next=/onboarding/
  // step/pos so the merchant bounces straight back to where they were.
  // Allowlist same as Register.tsx — only known internal paths survive.
  const ALLOWED_NEXT_PATHS = new Set<string>([
    "/onboarding/step/pos",
    "/onboarding/step/delivery",
    "/onboarding/step/menu",
    "/onboarding/step/inventory",
    "/onboarding/step/details",
    "/onboarding/step/activate",
    "/app/dashboard",
  ]);
  function safeNext(raw: string | null): string | null {
    if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return null;
    const [path] = raw.split("?", 1);
    return ALLOWED_NEXT_PATHS.has(path) ? raw : null;
  }
  const redirectTo =
    safeNext(searchParams.get("next")) ??
    (location.state as LocationState | null)?.from?.pathname ??
    "/app/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!email.trim() || !password.trim()) return;

    setLoading(true);
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // Surface the server's rate-limit message so merchants see the
      // "try again in 15 minutes" signal instead of a generic failure.
      // Backend HTTPExceptions are shaped { detail: { error, detail } } —
      // the human-readable string lives under `detail.detail`.
      const ax = err as AxiosError<{ detail?: { detail?: string } | string }>;
      const status = ax.response?.status;
      let message = "Invalid email or password. Please try again.";
      if (status === 429) {
        const detail = ax.response?.data?.detail;
        const serverMsg =
          typeof detail === "string"
            ? detail
            : detail?.detail;
        message =
          serverMsg ??
          "Too many failed attempts. Please try again in 15 minutes.";
      }
      setFormError(message);
      toast({
        variant: "destructive",
        title: "Login failed",
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-serif">
            Welcome back
          </h1>
          <p className="text-muted-foreground">
            Sign in to your Prep account
          </p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-6">
          {/* Form (Block C / C4: Sign in with Google removed; email +
              password is the sole entry path. AuthContext.loginWithGoogle
              + the /auth/callback route remain mounted but unreachable
              from any current UI surface; see AuthContext.tsx @deprecated
              JSDoc.) */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-invalid={Boolean(formError)}
                aria-describedby={formError ? "login-form-error" : undefined}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  aria-invalid={Boolean(formError)}
                  aria-describedby={formError ? "login-form-error" : undefined}
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  tabIndex={0}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {formError ? (
              <p
                id="login-form-error"
                role="alert"
                aria-live="polite"
                className="text-sm text-destructive"
              >
                {formError}
              </p>
            ) : null}

            <Button
              type="submit"
              className="w-full h-12 text-base bg-primary hover:bg-primary/90"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : null}
              Sign in
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link
            to="/register"
            className="font-medium text-primary hover:underline"
          >
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
